import { useEffect, useState, useCallback, useMemo } from "react";
import { getEmployeeDropdown } from "../../api/employeeApi";
import PremiumLeaveCard from "../../components/PremiumLeaveCard";
import {
  setLeaveBalance,
  getLeaveSummary,
  getLeaveBalance,
  getLeaveTypes,
} from "../../api/leaveApi";

/* ─────────── helper: pick a colour theme by leave-type name ─────────── */
const pickColor = (name = "") => {
  const n = name.toLowerCase();
  if (n.includes("casual")) return "casual";
  if (n.includes("sick")) return "sick";
  if (n.includes("compensatory") || n.includes("comp")) return "compensatory";
  if (n.includes("loss") || n.includes("lop")) return "taken";
  return "default";
};

const LeaveBalancePage = () => {
  const [tab, setTab] = useState("ALL"); // ALL | INDIVIDUAL
  const [employees, setEmployees] = useState([]);
  const [employee, setEmployee] = useState(""); // email
  const [search, setSearch] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);

  const [leaveTypes, setLeaveTypes] = useState([]);
  const [balances, setBalances] = useState({});
  const [loading, setLoading] = useState(false);

  /* summary cards for currently active view */
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  /* ── Load employees & leave types once ── */
  useEffect(() => {
    getEmployeeDropdown().then((res) => setEmployees(res.data || []));
    getLeaveTypes().then((res) => setLeaveTypes(res.data || []));
  }, []);

  /* ── Refresh summary cards (ALL or INDIVIDUAL) ── */
  const refreshSummary = useCallback(
    (targetEmail) => {
      const key = targetEmail || (tab === "ALL" ? "all" : employee);
      if (!key) return;
      setSummaryLoading(true);
      getLeaveSummary(key)
        .then((res) => setSummary(res.data || null))
        .catch(() => setSummary(null))
        .finally(() => setSummaryLoading(false));
    },
    [tab, employee]
  );

  /* ── Re-fetch whenever tab or selected employee changes ── */
  useEffect(() => {
    setSummary(null);
    setBalances({});

    if (tab === "ALL") {
      /* pre-fill the form with the current global default balance */
      getLeaveBalance()
        .then((res) => setBalances(res.data || {}))
        .catch(() => {});
      /* show aggregated summary cards */
      refreshSummary("all");
    } else if (tab === "INDIVIDUAL" && employee) {
      getLeaveSummary(employee)
        .then((res) => {
          setSummary(res.data || null);
          setBalances(res.data || {});
        })
        .catch(() => setSummary(null));
    }
  }, [tab, employee]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ─────────── SAVE ─────────── */
  const handleSave = async () => {
    const payload = {};
    for (const lt of leaveTypes) {
      const key = `${lt.name.toLowerCase().replace(/ /g, "_")}_leave`;
      const val = Number(
        balances[key] !== undefined && balances[key] !== null
          ? balances[key]
          : 0
      );
      if (val < 0) {
        alert(`Please enter a valid value for ${lt.name}`);
        return;
      }
      payload[key] = val;
    }

    if (tab === "INDIVIDUAL" && !employee) {
      alert("Please select an employee");
      return;
    }

    try {
      setLoading(true);

      if (tab === "ALL") {
        await setLeaveBalance(payload);
        alert("Leave balance updated for all employees");
        /* refresh aggregated cards immediately */
        refreshSummary("all");
      } else {
        await setLeaveBalance({ employee, ...payload });
        alert("Leave balance updated for selected employee");
        /* refresh per-employee cards */
        getLeaveSummary(employee).then((res) => setSummary(res.data || null));
      }
    } catch (err) {
      console.error(err);
      alert("Failed to update leave balance");
    } finally {
      setLoading(false);
    }
  };

  /* ─────────── helpers ─────────── */
  const filteredEmployees = employees.filter((e) =>
    `${e.employee_code || ""} ${e.full_name || ""} ${e.email}`
      .toLowerCase()
      .includes(search.toLowerCase())
  );

  const summaryCards = summary
    ? Object.keys(summary)
        .filter((k) => k.endsWith("_leave"))
        .map((key) => {
          const baseKey = key.slice(0, -6); // remove "_leave"
          const allowed = summary[key] ?? 0;
          const used = summary[`${baseKey}_used`] ?? 0;
          const displayTitle =
            baseKey
              .replace(/_/g, " ")
              .replace(/\b\w/g, (c) => c.toUpperCase()) + " Leave";
          return { key, displayTitle, allowed, used };
        })
    : [];

  const totalLeave = useMemo(() => {
    if (!summary) return 0;
    let sum = 0;
    Object.keys(summary).forEach((key) => {
      if (key.endsWith("_leave")) {
        sum += Number(summary[key] || 0);
      }
    });
    return sum;
  }, [summary]);

  const takenLeave = useMemo(() => {
    if (!summary) return 0;
    let sum = 0;
    Object.keys(summary).forEach((key) => {
      if (key.endsWith("_used")) {
        sum += Number(summary[key] || 0);
      }
    });
    return sum;
  }, [summary]);

  const balanceLeave = useMemo(() => {
    return Math.max(totalLeave - takenLeave, 0);
  }, [totalLeave, takenLeave]);

  /* ═══════════════════════ RENDER ═══════════════════════ */
  return (
    <div className="space-y-8">
      {/* ── Page title ── */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 tracking-tight">
          Leave Balance Management
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Set and review leave entitlements for all or individual employees.
        </p>
      </div>

      {/* ── Tabs ── */}
      <div className="flex gap-1 border-b border-gray-200">
        {["ALL", "INDIVIDUAL"].map((t) => (
          <button
            key={t}
            onClick={() => {
              setTab(t);
              setEmployee("");
              setSearch("");
            }}
            className={`px-5 py-2.5 text-sm font-semibold rounded-t-lg transition-all ${
              tab === t
                ? "border-b-2 border-blue-600 text-blue-600 bg-blue-50/50"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            {t === "ALL" ? "All Employees" : "Individual Employee"}
          </button>
        ))}
      </div>

      {/* ── Premium Leave Balance Cards ── */}
      {summaryLoading ? (
        /* skeleton */
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {[...Array(4)].map((_, i) => (
            <div
              key={i}
              className="animate-pulse rounded-2xl border bg-white p-6 h-36 shadow-sm"
            >
              <div className="h-4 bg-gray-200 rounded w-1/2 mb-4" />
              <div className="h-8 bg-gray-200 rounded w-1/3 mb-4" />
              <div className="h-2 bg-gray-200 rounded w-full" />
            </div>
          ))}
        </div>
      ) : summaryCards.length > 0 ? (
        tab === "ALL" ? (
          /* Render ALL tab cards in simple mode showing only default values */
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {summaryCards.map(({ key, displayTitle, allowed, used }) => (
              <PremiumLeaveCard
                key={key}
                title={displayTitle}
                allowed={allowed}
                used={used}
                value={allowed}
                color={pickColor(displayTitle)}
                simple
              />
            ))}
          </div>
        ) : (
          /* Render INDIVIDUAL tab cards with totals (simple) + individual leave-types (full) */
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <PremiumLeaveCard title="Total Leave" value={totalLeave} color="total" simple />
              <PremiumLeaveCard title="Leave Taken" value={takenLeave} color="taken" simple />
              <PremiumLeaveCard title="Balance Leave" value={balanceLeave} color="balance" simple />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {summaryCards.map(({ key, displayTitle, allowed, used }) => (
                <PremiumLeaveCard
                  key={key}
                  title={displayTitle}
                  allowed={allowed}
                  used={used}
                  color={pickColor(displayTitle)}
                />
              ))}
            </div>
          </div>
        )
      ) : tab === "INDIVIDUAL" && !employee ? (
        /* prompt when no employee selected yet */
        <div className="flex flex-col items-center justify-center py-12 text-gray-400 bg-white rounded-2xl border shadow-sm">
          <svg className="w-12 h-12 mb-3 opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
              d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m9-4a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
          <p className="font-medium">Select an employee to view their leave balance</p>
        </div>
      ) : null}

      {/* ── Form card ── */}
      <div className="bg-white rounded-2xl border shadow-sm p-7 max-w-lg space-y-6">
        <h2 className="text-lg font-bold text-gray-800">
          {tab === "ALL" ? "Set Balance — All Employees" : "Set Balance — Individual"}
        </h2>

        {/* Employee picker (INDIVIDUAL only) */}
        {tab === "INDIVIDUAL" && (
          <div className="relative">
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
              Employee
            </label>
            <input
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setShowDropdown(true);
              }}
              onFocus={() => setShowDropdown(true)}
              placeholder="Search by code, name, or email…"
              className="w-full border border-gray-200 bg-gray-50 px-3 py-2.5 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition"
            />
            {showDropdown && (
              <div className="absolute z-20 w-full bg-white border border-gray-200 rounded-xl shadow-lg max-h-56 overflow-y-auto mt-1">
                {filteredEmployees.length === 0 ? (
                  <div className="p-3 text-sm text-gray-400 text-center">No employees found</div>
                ) : (
                  filteredEmployees.map((e) => (
                    <div
                      key={e.email}
                      onClick={() => {
                        setEmployee(e.email);
                        setSearch(`${e.employee_code || ""} ${e.full_name || ""}`.trim());
                        setShowDropdown(false);
                      }}
                      className="px-4 py-3 cursor-pointer hover:bg-blue-50 transition border-b last:border-0"
                    >
                      <p className="font-semibold text-sm text-gray-800">
                        {e.employee_code} — {e.full_name}
                      </p>
                      <p className="text-xs text-gray-400">{e.email}</p>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        )}

        {/* Leave type inputs */}
        <div className="space-y-4">
          {leaveTypes.map((lt) => {
            const keySnake = lt.name.toLowerCase().replace(/ /g, "_");
            const inputKey = `${keySnake}_leave`;
            return (
              <div key={lt.id}>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                  {lt.name}
                </label>
                <input
                  type="number"
                  min="0"
                  value={
                    balances[inputKey] !== undefined &&
                    balances[inputKey] !== null
                      ? balances[inputKey]
                      : 0
                  }
                  onChange={(e) =>
                    setBalances({
                      ...balances,
                      [inputKey]:
                        e.target.value === ""
                          ? 0
                          : Number(e.target.value),
                    })
                  }
                  className="w-full border border-gray-200 bg-gray-50 px-3 py-2.5 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition"
                />
              </div>
            );
          })}
        </div>

        <button
          onClick={handleSave}
          disabled={loading}
          className="w-full bg-blue-600 hover:bg-blue-700 active:scale-95 text-white font-semibold px-5 py-2.5 rounded-lg transition-all disabled:opacity-50 shadow"
        >
          {loading ? "Saving…" : "Save Leave Balance"}
        </button>
      </div>
    </div>
  );
};

export default LeaveBalancePage;